import React from 'react'
import { Badge, Button, Dropdown, Tooltip } from 'antd'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import FilterOutlined from '@ant-design/icons/FilterOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import SyncOutlined from '@ant-design/icons/SyncOutlined'
import ThunderboltOutlined from '@ant-design/icons/ThunderboltOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'

import { VCN_SELECTION_MAX } from '../../api/dto/vcn-selection.dto'
import { VcnManagementSearchField } from './VcnManagementSearchField'
import {
  Container,
  LeftSection,
  RightSection,
} from './VcnManagementTopbar.styles'

export const EXPORT_ALL_LIMIT = 1000

export interface VcnManagementTopbarProps {
  searchInput: string
  onSearchInputChange: (value: string) => void
  onSearchSubmit: (value: string) => void
  activeFilterCount: number
  onToggleFilters: () => void
  onRefresh: () => void
  onBulkUpload: () => void
  onExportSelected: () => void
  onExportAll: () => void
  onTerminateSelected: () => void
  onTerminateAndNewSelected: () => void
  onCreateNewSelected: () => void
  selectedCount: number
  total: number
  isBusy: boolean
  isExporting: boolean
  isActioning: boolean
  /** When true, a shared bulk job is running — button shows progress affordance. */
  isBulkJobActive?: boolean
  /** When true, a dashboard-metric filter is active — normal filters are disabled. */
  metricFilterActive?: boolean
}

export function VcnManagementTopbar({
  searchInput,
  onSearchInputChange,
  onSearchSubmit,
  activeFilterCount,
  onToggleFilters,
  onRefresh,
  onBulkUpload,
  onExportSelected,
  onExportAll,
  onTerminateSelected,
  onTerminateAndNewSelected,
  onCreateNewSelected,
  selectedCount,
  total,
  isBusy,
  isExporting,
  isActioning,
  isBulkJobActive = false,
  metricFilterActive = false,
}: VcnManagementTopbarProps) {
  const exportAllDisabled = total > EXPORT_ALL_LIMIT
  const actionDisabled = selectedCount === 0 || isBulkJobActive

  const exportAllLimitHint = metricFilterActive
    ? 'Use Export Selected, or clear the dashboard metric filter and apply Filters to reduce the row count.'
    : 'Apply more filters first.'

  const exportAllDisabledTooltip = exportAllDisabled
    ? `Export All is disabled — ${total.toLocaleString()} rows exceeds the ${EXPORT_ALL_LIMIT.toLocaleString()} row limit. ${exportAllLimitHint}`
    : undefined

  const actionMenuItems = [
    {
      key: 'terminate',
      label: selectedCount > 0 ? `Terminate (${selectedCount})` : 'Terminate',
      disabled: actionDisabled,
      icon: <ThunderboltOutlined />,
    },
    {
      key: 'terminate-and-new',
      label:
        selectedCount > 0
          ? `Terminate and New (${selectedCount})`
          : 'Terminate and New',
      disabled: actionDisabled,
      icon: <ThunderboltOutlined />,
    },
    {
      key: 'create-new',
      label: selectedCount > 0 ? `Create New (${selectedCount})` : 'Create New',
      disabled: actionDisabled,
      icon: <ThunderboltOutlined />,
    },
  ]

  const exportMenuItems = [
    {
      key: 'selected',
      label:
        selectedCount > 0
          ? `Export Selected (${selectedCount})`
          : 'Export Selected',
      disabled: selectedCount === 0,
      icon: <DownloadOutlined />,
    },
    {
      key: 'all',
      label: exportAllDisabled
        ? metricFilterActive
          ? `Export All (${total.toLocaleString()} rows — use Export Selected or clear metric filter)`
          : `Export All (${total.toLocaleString()} rows — filter to ≤${EXPORT_ALL_LIMIT.toLocaleString()})`
        : 'Export All',
      disabled: exportAllDisabled,
      icon: <DownloadOutlined />,
    },
  ]

  return (
    <Container>
      <LeftSection>
        <VcnManagementSearchField
          value={searchInput}
          onChange={onSearchInputChange}
          onSearch={onSearchSubmit}
          isDisabled={isBusy || metricFilterActive}
        />
      </LeftSection>

      <RightSection>
        <Button
          icon={<SyncOutlined spin={isBusy} />}
          iconPosition="end"
          onClick={onRefresh}
          loading={isBusy}
          disabled={isBusy}
          size="large"
        >
          Refresh
        </Button>

        <Tooltip
          title={
            isBulkJobActive
              ? 'A bulk job is in progress — row actions are disabled'
              : actionDisabled
                ? `Select rows to perform an action (max ${VCN_SELECTION_MAX})`
                : undefined
          }
        >
          <span>
            <Dropdown
              menu={{
                items: actionMenuItems,
                onClick: ({ key }) => {
                  if (key === 'terminate') onTerminateSelected()
                  else if (key === 'terminate-and-new')
                    onTerminateAndNewSelected()
                  else if (key === 'create-new') onCreateNewSelected()
                },
              }}
              disabled={isBusy || isActioning || actionDisabled}
            >
              <Button
                type="primary"
                icon={
                  isActioning ? (
                    <LoadingOutlined spin />
                  ) : (
                    <ThunderboltOutlined />
                  )
                }
                iconPosition="end"
                disabled={isBusy || isActioning || actionDisabled}
                size="large"
              >
                Actions
              </Button>
            </Dropdown>
          </span>
        </Tooltip>

        <Tooltip title={exportAllDisabledTooltip}>
          <Dropdown
            menu={{
              items: exportMenuItems,
              onClick: ({ key }) => {
                if (key === 'selected') onExportSelected()
                else if (key === 'all') onExportAll()
              },
            }}
            disabled={isBusy || isExporting}
          >
            <Button
              icon={
                isExporting ? <LoadingOutlined spin /> : <DownloadOutlined />
              }
              iconPosition="end"
              disabled={isBusy || isExporting}
              size="large"
            >
              Export
            </Button>
          </Dropdown>
        </Tooltip>

        <Button
          type="default"
          icon={isBulkJobActive ? <LoadingOutlined spin /> : <UploadOutlined />}
          iconPosition="end"
          onClick={onBulkUpload}
          disabled={(isBusy || isActioning) && !isBulkJobActive}
          size="large"
        >
          {isBulkJobActive ? 'Bulk job…' : 'Bulk upload'}
        </Button>

        <Badge count={activeFilterCount} offset={[-5, 5]}>
          <Button
            icon={<FilterOutlined />}
            iconPosition="end"
            onClick={onToggleFilters}
            disabled={metricFilterActive}
            size="large"
          >
            Filters
          </Button>
        </Badge>
      </RightSection>
    </Container>
  )
}
